import React, { useEffect, useMemo, useState } from 'react';
import { FaqContainerStyle } from './FaqContent.style';
import { MostCommonQuestionsView } from './views/MostCommonQuestionsView';
import { AllCategoriesView } from './views/AllCategoriesView';
import { CategoryQuestionsView } from './views/CategoryQuestionsView';
import { getFaqCategories, getFaqData, getFaqCategoriesLoading } from '@src/middleware/selectors/faqSelector';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFaqCategories, fetchFaqListData } from '@src/middleware/actions/Faq/faqActions';
import { preloadCommonModules } from '@src/utility/moduleLoader';
import { checkIsInternal, checkIsSimulationMode } from '@src/utility/createStorage';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';

type ViewType = 'mostCommon' | 'allCategories' | 'categoryQuestions';

export const FaqContent: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewType>('allCategories');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [expandedQuestions, setExpandedQuestions] = useState<number[]>([]);
  const [simulationModeToggle, setSimulationModeToggle] = useState<number>(0);
  const [hasRequestedFaqData, setHasRequestedFaqData] = useState<boolean>(false);
  const dispatch = useDispatch();

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('parcelLoad'));
    preloadCommonModules().catch((error) => {
      console.error('Error preloading common modules:', error);
    });
  }, []);

  const dispatchFaqData = () => {
    setHasRequestedFaqData(true);
    dispatch(fetchFaqListData());
    dispatch(fetchFaqCategories());
  };

  useEffect(() => {
    dispatchFaqData();
  }, [dispatch]);

  useEffect(() => {
    // Handle faqDrawerClosed event from external parcel
    const handleDrawerClosed = () => {
      setExpandedQuestions([]);
    };

    const handleDrawerOpened = () => {
      dispatchFaqData();
    };

    // Handle simulation mode events
    const handleExitProviderView = () => {
      setSimulationModeToggle(prev => prev + 1);
    };

    window.addEventListener('faqDrawerClosed', handleDrawerClosed);
    window.addEventListener('faqDrawerOpened', handleDrawerOpened);
    window.addEventListener('ExitProviderView', handleExitProviderView);

    return () => {
      window.removeEventListener('faqDrawerClosed', handleDrawerClosed);
      window.removeEventListener('faqDrawerOpened', handleDrawerOpened);
      window.removeEventListener('ExitProviderView', handleExitProviderView);
    };
  }, [dispatch]);

  const toggleQuestion = (index: number) => {
    setExpandedQuestions(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleCategoryClick = (category: { CategorySourceId: string; CategorySource: string }) => {
    setSelectedCategory(category.CategorySource);
    setSelectedCategoryId(String(category.CategorySourceId ?? ''));
    dispatch(fetchFaqListData());
    setCurrentView('categoryQuestions');
    setExpandedQuestions([]); // Reset expanded questions on view change
  };

  const handleAllCategoriesClick = () => {
    setCurrentView('allCategories');
    setExpandedQuestions([]); // Reset expanded questions on view change
  };

  const handleMostCommonQuestionsClick = () => {
    setCurrentView('mostCommon');
    setExpandedQuestions([]); // Reset expanded questions on view change
  };

  const handleContactUs = () => {
    // Implement contact us functionality
    window.dispatchEvent(new CustomEvent('openFaqConatactUsDrawer'));
  };
  const faqCategories = useSelector(getFaqCategories);
  const faqData = useSelector(getFaqData);
  const faqCategoriesLoading = useSelector(getFaqCategoriesLoading);
  const isInternalUser = checkIsInternal();
  const isSimulationMode = useMemo(() => {
    const checkIsSimulationModeValue = checkIsSimulationMode()
    return checkIsSimulationModeValue;
  }, [simulationModeToggle]);

  const normalizedFaqCategories = useMemo(() => {
    return (faqCategories || [])
      .map((category: any) => ({
        CategorySourceId: String(
          category?.CategorySourceId ?? category?.categorySourceId ?? category?.id ?? ''
        ),
        CategorySource: String(
          category?.CategorySource ?? category?.categorySource ?? category?.name ?? ''
        ).trim(),
      }))
      .filter((category: any) => category.CategorySource !== '');
  }, [faqCategories]);

  const categoriesToUse = useMemo(() => {
  if (isSimulationMode || !checkIsInternal()) {
    return normalizedFaqCategories.filter(
      c => c.CategorySource !== "Provider Negotiation Briefing"
    );
  }

  return normalizedFaqCategories;
}, [normalizedFaqCategories, isInternalUser, isSimulationMode]);

  const normalizedFaqData = useMemo(() => {
    return (faqData || []).map((item: any) => {
      const metric = String(item?.MetricAvailableIn ?? item?.MetricAvailbleIn ?? '').trim();
      return {
        ...item,
        QuestionId: item?.QuestionId ?? item?.questionId ?? item?.id,
        Question: item?.Question ?? item?.question ?? '',
        Answer: item?.Answer ?? item?.answer ?? '',
        MostCommonQuestion: Boolean(item?.MostCommonQuestion ?? item?.mostCommonQuestion),
        CategorySource: item?.CategorySource ?? item?.categorySource ?? '',
        CategorySourceId: String(item?.CategorySourceId ?? item?.categorySourceId ?? item?.CategorySourceIds ?? ''),
        CategorySourceIds: String(item?.CategorySourceIds ?? item?.categorySourceIds ?? item?.CategorySourceId ?? ''),
        MetricAvailableIn: metric,
      };
    });
  }, [faqData]);

  const userFaqData = useMemo(() => {
    if (isInternalUser && !isSimulationMode) {
      return normalizedFaqData.filter((item: any) => {
        const metric = String(item?.MetricAvailableIn ?? '').toLowerCase();
        return metric.includes('internal') || metric === '';
      });
    } else {
      return normalizedFaqData.filter((item: any) => {
        const metric = String(item?.MetricAvailableIn ?? '').toLowerCase();
        return metric.includes('external') || metric === '';
      });
    }
  }, [normalizedFaqData, isInternalUser, isSimulationMode]);
  // Get FAQ items where MostCommonQuestion is true for most common view
  const faqItems = useMemo(() => userFaqData?.filter(item => item.MostCommonQuestion === true) || [], [userFaqData]);

  // Get FAQ items filtered by selected category
  const categoryFaqItems = useMemo(() => {
    const selectedId = String(selectedCategoryId || '');
    return userFaqData.filter((item: any) => {
      const itemSingleId = String(item?.CategorySourceId ?? '');
      const itemIds = String(item?.CategorySourceIds ?? '')
        .split(',')
        .map((id: string) => id.trim())
        .filter(Boolean);
      const byId = selectedId !== '' && (itemSingleId === selectedId || itemIds.includes(selectedId));
      const byName = String(item?.CategorySource ?? '').trim().toLowerCase() === selectedCategory.trim().toLowerCase();
      return byId || byName;
    });
  }, [userFaqData, selectedCategory, selectedCategoryId]);

  const shouldShowInitialLoader =
    normalizedFaqCategories.length === 0 &&
    (!hasRequestedFaqData || faqCategoriesLoading);

  if (shouldShowInitialLoader) {
    return (
      <FaqContainerStyle>
        <div
          style={{
            width: '100%',
            minHeight: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingSpinner size="$lg" isLoading ariaLoadingLabel="Loading FAQ categories" />
        </div>
      </FaqContainerStyle>
    );
  }

  return (
    <FaqContainerStyle>
      {currentView === 'mostCommon' && (
        <MostCommonQuestionsView
          faqItems={faqItems}
          expandedQuestions={expandedQuestions}
          onToggleQuestion={toggleQuestion}
          onAllCategoriesClick={handleAllCategoriesClick}
          onContactUs={handleContactUs}
        />
      )}
      
      {currentView === 'allCategories' && (
        <AllCategoriesView
          faqCategories={categoriesToUse}
          onCategoryClick={handleCategoryClick}
          onMostCommonQuestionsClick={handleMostCommonQuestionsClick}
          onContactUs={handleContactUs}
        />
      )}
      
      {currentView === 'categoryQuestions' && (
        <CategoryQuestionsView
          faqItems={categoryFaqItems}
          expandedQuestions={expandedQuestions}
          selectedCategory={selectedCategory}
          onToggleQuestion={toggleQuestion}
          onAllCategoriesClick={handleAllCategoriesClick}
          onMostCommonQuestionsClick={handleMostCommonQuestionsClick}
          onContactUs={handleContactUs}
        />
      )}
    </FaqContainerStyle>
  );
};

export default FaqContent;
